import { LitSquadInventoryEntry } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowRightLeft, Hash, Package, Plus, Trash2, UserCircle } from 'lucide-react';

import {
  getLitSquadSpecies,
  isLitSquadCharacter,
  isSafeImageUri,
  resolveLitSquadDisplayName,
  resolveLitSquadThumbnail,
} from '../utils';

interface InventoryItemCardProps {
  item: LitSquadInventoryEntry;
  onTransfer?: (item: LitSquadInventoryEntry) => void;
  onDelete?: (item: LitSquadInventoryEntry) => void;
  onClick?: (item: LitSquadInventoryEntry) => void;
  /** Set this character as the avatar base. Only invoked for species items. */
  onSetAvatarBase?: (item: LitSquadInventoryEntry) => void;
  /** Add this accessory on top of the in-progress avatar. Only invoked for non-species items. */
  onAddAvatarAccessory?: (item: LitSquadInventoryEntry) => void;
  /**
   * Lowercased species ("dog", "bear", …) of the avatar's base character,
   * or `null` when no base has been chosen yet. Accessories whose species
   * doesn't match the base hide the "Add to avatar" button — only matching
   * breeds fit on the silhouette. Pass undefined to skip species gating
   * entirely (used by tests and any caller that doesn't care about the
   * avatar editor flow).
   */
  currentAvatarBaseSpecies?: string | null;
}

export function InventoryItemCard({
  item,
  onTransfer,
  onDelete,
  onClick,
  onSetAvatarBase,
  onAddAvatarAccessory,
  currentAvatarBaseSpecies,
}: InventoryItemCardProps) {
  const displayName = resolveLitSquadDisplayName(item);
  const thumbnailUri = resolveLitSquadThumbnail(item);
  const isCharacter = isLitSquadCharacter(item);
  // For accessories, only show the avatar-add button when (a) a base
  // character is selected and (b) this accessory's species matches.
  // `currentAvatarBaseSpecies === undefined` means the caller opted out
  // of gating; preserve the old "always show" behavior in that case.
  const accessorySpecies = !isCharacter ? getLitSquadSpecies(item) : null;
  const accessoryFitsBase =
    currentAvatarBaseSpecies === undefined
      ? true
      : currentAvatarBaseSpecies !== null &&
        accessorySpecies !== null &&
        accessorySpecies === currentAvatarBaseSpecies;

  return (
    <Card
      className={`overflow-hidden transition-shadow hover:shadow-md ${onClick ? 'cursor-pointer' : ''}`}
      onClick={() => onClick?.(item)}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick(item);
              }
            }
          : undefined
      }
    >
      <div className="aspect-square bg-muted flex items-center justify-center">
        {isSafeImageUri(thumbnailUri) ? (
          <img src={thumbnailUri!} alt={displayName} className="w-full h-full object-cover" />
        ) : (
          <Package className="h-12 w-12 text-muted-foreground" />
        )}
      </div>
      <CardContent className="p-3">
        <h3 className="font-medium text-sm truncate" title={displayName}>
          {displayName}
        </h3>
        <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
          <span className="truncate">{item.speciesName}</span>
          {item.instanceNumber != null && (
            <span className="flex items-center gap-0.5 ml-auto">
              <Hash className="h-3 w-3" />
              {item.instanceNumber}
            </span>
          )}
        </div>
        {/*
         * Two-row action stack so the avatar action (primary) doesn't
         * compete with Transfer/Burn (secondary) for horizontal space.
         * The card is narrow at the smallest grid breakpoint (2 cols on
         * sm:), and three flex-1 buttons in a single row would clip the
         * Transfer label and push the icon-only Burn off the right edge.
         */}
        <div className="flex flex-col gap-1 mt-2" onClick={(e) => e.stopPropagation()}>
          {isCharacter && onSetAvatarBase && (
            <Button
              variant="secondary"
              size="sm"
              className="w-full h-7 text-xs"
              onClick={() => onSetAvatarBase(item)}
              aria-label={`Set ${displayName} as avatar`}
            >
              <UserCircle className="h-3 w-3 mr-1" />
              Set as avatar
            </Button>
          )}
          {!isCharacter && onAddAvatarAccessory && accessoryFitsBase && (
            <Button
              variant="secondary"
              size="sm"
              className="w-full h-7 text-xs"
              onClick={() => onAddAvatarAccessory(item)}
              aria-label={`Add ${displayName} to avatar`}
            >
              <Plus className="h-3 w-3 mr-1" />
              Add to avatar
            </Button>
          )}
          {(onTransfer || onDelete) && (
            <div className="flex gap-1">
              {onTransfer && (
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 h-7 text-xs min-w-0"
                  onClick={() => onTransfer(item)}
                  aria-label={`Transfer ${displayName}`}
                >
                  <ArrowRightLeft className="h-3 w-3 mr-1 shrink-0" />
                  <span className="truncate">Transfer</span>
                </Button>
              )}
              {onDelete && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs text-destructive shrink-0"
                  onClick={() => onDelete(item)}
                  aria-label={`Burn ${displayName}`}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
